// src/pages/maintenance/PmTemplateModal.js
// Create/edit a preventive-maintenance template — triggers on a calendar interval or a meter
// threshold. See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState } from 'react';
import maintenanceService from '../../services/maintenanceService';
import userService from '../../services/userService';
import SearchableSelect from '../../components/SearchableSelect';
import VendorPicker from './VendorPicker';

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function PmTemplateModal({ template, onSave, onClose }) {
  const [assets, setAssets] = useState([]);
  const [meters, setMeters] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: template?.title || '',
    description: template?.description || '',
    assetId: template?.assetId || '',
    vendorId: template?.vendorId || '',
    defaultAssigneeId: template?.defaultAssigneeId || '',
    priority: template?.priority || 'medium',
    estimatedHours: template?.estimatedHours || '',
    estimatedCost: template?.estimatedCost || '',
    triggerType: template?.triggerType || 'calendar',
    calendarIntervalDays: template?.calendarIntervalDays || 30,
    meterId: template?.meterId || '',
    meterThreshold: template?.meterThreshold || '',
    leadTimeDays: template?.leadTimeDays || 3,
    sqfProgramRef: template?.sqfProgramRef || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    maintenanceService.getAssets().then(setAssets);
    userService.listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    if (form.assetId) {
      maintenanceService.getAssetMeters(form.assetId).then(setMeters);
    } else {
      setMeters([]);
    }
  }, [form.assetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.assetId) { setError('Title and asset are required.'); return; }
    if (form.triggerType === 'meter' && (!form.meterId || form.meterThreshold === '')) {
      setError('Meter and threshold are required for a meter-triggered template.');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        vendorId: form.vendorId || null,
        defaultAssigneeId: form.defaultAssigneeId || null,
        estimatedHours: form.estimatedHours !== '' ? parseFloat(form.estimatedHours) : null,
        estimatedCost: form.estimatedCost !== '' ? parseFloat(form.estimatedCost) : null,
        calendarIntervalDays: form.triggerType === 'calendar' ? parseInt(form.calendarIntervalDays) : null,
        meterId: form.triggerType === 'meter' ? form.meterId : null,
        meterThreshold: form.triggerType === 'meter' ? parseFloat(form.meterThreshold) : null,
        leadTimeDays: parseInt(form.leadTimeDays) || 3,
      };
      const result = template?.id
        ? await maintenanceService.updatePmTemplate(template.id, payload)
        : await maintenanceService.createPmTemplate(payload);
      onSave(result);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save PM template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{template?.id ? 'Edit' : 'New'} PM Template</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Title <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Quarterly oven inspection" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Priority</label>
                  <SearchableSelect
                    options={PRIORITY_OPTIONS.map(p => ({ value: p, label: labelize(p) }))}
                    value={form.priority}
                    onChange={val => set('priority', val || 'medium')}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Description / Procedure</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Asset <span className="text-danger">*</span></label>
                  <SearchableSelect
                    options={assets.map(a => ({ value: a.id, label: a.name }))}
                    value={form.assetId}
                    onChange={val => set('assetId', val || '')}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Vendor (optional)</label>
                  <VendorPicker value={form.vendorId} onChange={val => set('vendorId', val || '')} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Default Assignee (optional)</label>
                  <SearchableSelect
                    options={users.map(u => ({ value: u.id, label: u.username || `${u.firstName || ''} ${u.lastName || ''}`.trim() }))}
                    value={form.defaultAssigneeId}
                    onChange={val => set('defaultAssigneeId', val || '')}
                    placeholder="Unassigned — generated WOs land in the general queue"
                  />
                  <div className="form-text" style={{ fontSize: 11 }}>
                    Every work order generated from this template (auto or "Generate WO Now") is
                    assigned to this person automatically.
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Trigger</label>
                  <SearchableSelect
                    options={[{ value: 'calendar', label: 'Calendar Interval' }, { value: 'meter', label: 'Meter Threshold' }]}
                    value={form.triggerType}
                    onChange={val => set('triggerType', val || 'calendar')}
                  />
                </div>
                {form.triggerType === 'calendar' ? (
                  <div className="col-md-4">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Repeat Every (days)</label>
                    <input className="form-control form-control-sm" value={form.calendarIntervalDays} onChange={e => set('calendarIntervalDays', e.target.value)} />
                  </div>
                ) : (
                  <>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Meter</label>
                      <SearchableSelect
                        options={meters.map(m => ({ value: m.id, label: `${m.name} (${m.unit})` }))}
                        value={form.meterId}
                        onChange={val => set('meterId', val || '')}
                        placeholder={form.assetId ? 'Select meter' : 'Pick an asset first'}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Trigger Every (units)</label>
                      <input className="form-control form-control-sm" value={form.meterThreshold} onChange={e => set('meterThreshold', e.target.value)} />
                    </div>
                  </>
                )}
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Lead Time (days)</label>
                  <input className="form-control form-control-sm" value={form.leadTimeDays} onChange={e => set('leadTimeDays', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Est. Hours</label>
                  <input className="form-control form-control-sm" value={form.estimatedHours} onChange={e => set('estimatedHours', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Est. Cost ($)</label>
                  <input className="form-control form-control-sm" value={form.estimatedCost} onChange={e => set('estimatedCost', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>SQF Program Ref</label>
                  <input className="form-control form-control-sm" value={form.sqfProgramRef} onChange={e => set('sqfProgramRef', e.target.value)} placeholder="e.g. 11.2.3" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save Template'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
